import { ModalHeader } from '@pique/component/auth-components';
import { authAtom } from '@pique/core/atoms';
import { Trans, useI18n } from '@pique/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useAtomValue } from 'jotai';
import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { AuthService } from '../../../modules/cloud';
import type { AuthPanelProps } from './index';
import { OAuth } from './oauth';
import * as style from './style.css';

export const SignIn: FC<AuthPanelProps> = ({ onSignedIn }) => {
  const t = useI18n();
  const authService = useService(AuthService);
  const [searchParams] = useSearchParams();
  const { openModal } = useAtomValue(authAtom);

  useEffect(() => {
    const timeout = setInterval(() => {
      // revalidate session to get the latest status
      authService.session.revalidate();
    }, 3000);
    return () => {
      clearInterval(timeout);
    };
  }, [authService]);

  const loginStatus = useLiveData(authService.session.status$);
  // Avoid calling onSignedIn during render to prevent React warning about setState in render
  useEffect(() => {
    if (loginStatus === 'authenticated' && openModal) {
      onSignedIn?.();
    }
  }, [loginStatus, openModal, onSignedIn]);

  const forceConsent = useMemo(() => {
    const param = searchParams.get('force_consent');
    if (!param) {
      return false;
    }
    const normalized = param.toLowerCase();
    return normalized === '1' || normalized === 'true' || normalized === 'yes';
  }, [searchParams]);

  return (
    <>
      <ModalHeader
        title={t['com.pique.auth.sign.in']()}
        subTitle={t['com.pique.brand.affineCloud']()}
      />

      <OAuth
        redirectUri={searchParams.get('redirect_uri')}
        forceConsent={forceConsent}
      />

      <div className={style.authModalContent}>
        <div className={style.authMessage}>
          {/*prettier-ignore*/}
          <Trans i18nKey="com.pique.auth.sign.message">
            By clicking &quot;Continue with Google/Email&quot; above, you acknowledge that
            you agree to Pique&apos;s <a href="https://pique.so/terms" target="_blank" rel="noreferrer">Terms of Service</a> and <a href="https://pique.so/terms/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>.
          </Trans>
        </div>
      </div>
    </>
  );
};
